import { useEffect, useState } from "react";
import api, { fmtTRY, fmtDateTime } from "../lib/api";
import { toast } from "sonner";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Plus, Trash2, TrendingUp, TrendingDown, Wallet } from "lucide-react";

const KIND = {
  gelir: { label: "Gelir", color: "bg-emerald-600 text-white" },
  gider: { label: "Gider", color: "bg-rose-600 text-white" },
  tahsilat: { label: "Tahsilat", color: "bg-[#D97706] text-white" },
};
const METHOD = { nakit: "Nakit", havale: "Havale/EFT", cek: "Çek", kredi_karti: "Kredi Kartı" };
const CENTER = { servis: "Servis", satis: "Satış", genel: "Genel" };

const empty = { kind: "gelir", category: "", amount: 0, description: "", payment_method: "nakit", customer_id: "", cost_center: "genel" };

export default function FinancePage() {
  const [items, setItems] = useState([]);
  const [tab, setTab] = useState("all");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [customers, setCustomers] = useState([]);

  const load = () => api.get(`/transactions${tab !== "all" ? `?kind=${tab}` : ""}`).then((r) => setItems(r.data));
  useEffect(() => { load(); }, [tab]);
  useEffect(() => { api.get("/customers").then((r) => setCustomers(r.data)); }, []);

  const submit = async () => {
    if (!form.amount) return toast.error("Tutar gerekli");
    try {
      await api.post("/transactions", { ...form, amount: Number(form.amount) });
      toast.success("Kaydedildi"); setOpen(false); setForm(empty); load();
    } catch { toast.error("Kayıt başarısız"); }
  };

  const remove = async (id) => { if (window.confirm("Silinsin mi?")) { await api.delete(`/transactions/${id}`); toast.success("Silindi"); load(); } };

  const totals = items.reduce((acc, t) => { acc[t.kind] = (acc[t.kind] || 0) + Number(t.amount || 0); return acc; }, {});

  return (
    <div className="space-y-5" data-testid="finance-page">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-[#1B4D3E]">Muhasebe</div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight text-slate-900">Gelir · Gider · Tahsilat</h1>
        </div>
        <Button className="bg-[#1B4D3E] hover:bg-[#163e32] text-white gap-2" onClick={() => setOpen(true)} data-testid="add-tx-btn">
          <Plus size={16} /> Yeni Hareket
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Card className="border-slate-200"><CardContent className="p-4 flex items-center justify-between">
          <div><div className="text-xs uppercase tracking-widest font-bold text-slate-500">Gelir</div><div className="text-2xl font-extrabold text-emerald-700">{fmtTRY(totals.gelir || 0)}</div></div>
          <div className="h-10 w-10 rounded-xl bg-emerald-600 text-white grid place-items-center"><TrendingUp size={18} /></div>
        </CardContent></Card>
        <Card className="border-slate-200"><CardContent className="p-4 flex items-center justify-between">
          <div><div className="text-xs uppercase tracking-widest font-bold text-slate-500">Gider</div><div className="text-2xl font-extrabold text-rose-700">{fmtTRY(totals.gider || 0)}</div></div>
          <div className="h-10 w-10 rounded-xl bg-rose-600 text-white grid place-items-center"><TrendingDown size={18} /></div>
        </CardContent></Card>
        <Card className="border-slate-200"><CardContent className="p-4 flex items-center justify-between">
          <div><div className="text-xs uppercase tracking-widest font-bold text-slate-500">Tahsilat</div><div className="text-2xl font-extrabold text-[#D97706]">{fmtTRY(totals.tahsilat || 0)}</div></div>
          <div className="h-10 w-10 rounded-xl bg-[#D97706] text-white grid place-items-center"><Wallet size={18} /></div>
        </CardContent></Card>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="all" data-testid="fin-tab-all">Tümü</TabsTrigger>
          <TabsTrigger value="gelir" data-testid="fin-tab-gelir">Gelir</TabsTrigger>
          <TabsTrigger value="gider" data-testid="fin-tab-gider">Gider</TabsTrigger>
          <TabsTrigger value="tahsilat" data-testid="fin-tab-tahsilat">Tahsilat</TabsTrigger>
        </TabsList>
      </Tabs>

      <Card className="border-slate-200">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Tarih</TableHead>
                <TableHead>Tür</TableHead>
                <TableHead>Merkez</TableHead>
                <TableHead>Kategori</TableHead>
                <TableHead>Açıklama</TableHead>
                <TableHead>Ödeme</TableHead>
                <TableHead className="text-right">Tutar</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((t) => (
                <TableRow key={t.id} data-testid={`tx-row-${t.id}`}>
                  <TableCell className="text-xs">{fmtDateTime(t.date || t.created_at)}</TableCell>
                  <TableCell><Badge className={KIND[t.kind]?.color}>{KIND[t.kind]?.label}</Badge></TableCell>
                  <TableCell><Badge variant="outline" className="text-[10px]">{CENTER[t.cost_center] || "Genel"}</Badge></TableCell>
                  <TableCell>{t.category || "—"}</TableCell>
                  <TableCell className="max-w-xs truncate">{t.description || "—"}</TableCell>
                  <TableCell className="text-xs">{METHOD[t.payment_method] || t.payment_method}</TableCell>
                  <TableCell className={`text-right font-bold ${t.kind === "gider" ? "text-rose-600" : "text-emerald-700"}`}>{t.kind === "gider" ? "-" : "+"}{fmtTRY(t.amount)}</TableCell>
                  <TableCell><Button size="icon" variant="ghost" onClick={() => remove(t.id)} data-testid={`delete-tx-${t.id}`}><Trash2 size={14} className="text-rose-600" /></Button></TableCell>
                </TableRow>
              ))}
              {items.length === 0 && <TableRow><TableCell colSpan={8} className="text-center py-10 text-slate-500">Kayıt yok.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Yeni Hareket</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Tür</Label>
              <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
                <SelectTrigger data-testid="tx-kind"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="gelir">Gelir</SelectItem>
                  <SelectItem value="gider">Gider</SelectItem>
                  <SelectItem value="tahsilat">Tahsilat</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Ödeme</Label>
              <Select value={form.payment_method} onValueChange={(v) => setForm({ ...form, payment_method: v })}>
                <SelectTrigger data-testid="tx-method"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="nakit">Nakit</SelectItem>
                  <SelectItem value="havale">Havale/EFT</SelectItem>
                  <SelectItem value="cek">Çek</SelectItem>
                  <SelectItem value="kredi_karti">Kredi Kartı</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2"><Label>Kategori</Label><Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="Örn: Servis Personel Maaşı, Yakıt, Kira" data-testid="tx-cat" /></div>
            <div><Label>Masraf Merkezi</Label>
              <Select value={form.cost_center} onValueChange={(v) => setForm({ ...form, cost_center: v })}>
                <SelectTrigger data-testid="tx-center"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="servis">Servis</SelectItem>
                  <SelectItem value="satis">Satış</SelectItem>
                  <SelectItem value="genel">Genel</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Tutar (₺)</Label><Input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="tx-amount" /></div>
            <div className="col-span-2">
              <Label>Müşteri (opsiyonel)</Label>
              <Select value={form.customer_id || ""} onValueChange={(v) => setForm({ ...form, customer_id: v })}>
                <SelectTrigger data-testid="tx-customer"><SelectValue placeholder="Seç" /></SelectTrigger>
                <SelectContent>{customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="col-span-2"><Label>Açıklama</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="tx-desc" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>İptal</Button>
            <Button className="bg-[#1B4D3E] hover:bg-[#163e32]" onClick={submit} data-testid="save-tx-btn">Kaydet</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
